import {
  useCallback,
  type ChangeEvent,
  type FocusEventHandler,
  type FunctionComponent,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ClockIcon from '@atlaskit/icon/core/clock';
import { Button } from '@trello/nachos/button';

import { AutosizeTextarea } from 'app/src/components/AutosizeTextarea';

import * as styles from './WorkingHours.module.less';

interface WorkingHoursProps {
  onNext: () => void;
  workingHours: string;
  onWorkingHoursChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

export const WorkingHours: FunctionComponent<WorkingHoursProps> = ({
  onNext,
  workingHours,
  onWorkingHoursChange,
}) => {
  const intl = useIntl();
  const isContinueDisabled = workingHours.trim().length === 0;

  const onFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>((e) => {
    const length = e.currentTarget.value.length;
    e.currentTarget.setSelectionRange(length, length);
  }, []);

  return (
    <div className={styles.container}>
      <h2>
        <FormattedMessage
          id="templates.smart_schedule.onboarding.working-hours-heading"
          defaultMessage="Set your working hours"
          description="Heading on the working hours onboarding page"
        />
      </h2>
      <p>
        <FormattedMessage
          id="templates.smart_schedule.onboarding.rules-help-description"
          defaultMessage="These rules help determine when focus times will be scheduled for you."
          description="Explanation of how the entered rules affect scheduling"
        />
      </p>
      <div className={styles.textfieldContainer}>
        <label className={styles.label} htmlFor="working-hours-start">
          {intl.formatMessage({
            id: 'templates.smart_schedule.onboarding.working-hours-label',
            defaultMessage: 'What are your working hours?',
            description: 'Label for the working hours input',
          })}
        </label>
        <AutosizeTextarea
          className={styles.textarea}
          id="working-hours-start"
          maxLength={512}
          value={workingHours}
          onChange={onWorkingHoursChange}
          onFocus={onFocus}
          shouldFocus
        />
      </div>
      <div className={styles.buttonContainer}>
        <Button
          appearance="primary"
          isDisabled={isContinueDisabled}
          onClick={onNext}
          iconBefore={<ClockIcon label="" />}
        >
          <FormattedMessage
            id="templates.smart_schedule.onboarding.add-working-hours"
            defaultMessage="Add working hours"
            description="Button to confirm the working hours entered"
          />
        </Button>
      </div>
    </div>
  );
};
